import type { AxiosRequestConfig } from 'axios';
import { injectable } from 'inversify';
import type { AttachmentId, DocumentId, DocumentRevision, DocumentWithChildren } from '../types';
import type {
  BaseDocumentChangeModel,
  BaseDocumentModel,
  UserAccessModel,
  DocumentAccess,
} from './models';
import type { CopyRequestArg, MoveRequestArg, UpdateResponse } from './types';
import { fileReadAsync } from '../utils';
import type { Migration } from './interfaces';
import { BaseCRUDRepository } from './BaseCRUD.repository';
import type { MeiliSearchConfig } from './repositories';

@injectable()
export class BaseDocumentsRepository<
  TModel extends BaseDocumentModel = BaseDocumentModel,
> extends BaseCRUDRepository<TModel> {
  protected migrator?: Migration<TModel>;

  async copy(body: CopyRequestArg, options?: AxiosRequestConfig): Promise<TModel> {
    const { data } = await this.httpClient.post<TModel>(`/copy`, body, options);

    return this.migrator ? this.migrator.apply(data) : data;
  }

  async move(body: MoveRequestArg, options?: AxiosRequestConfig): Promise<TModel[]> {
    const { data } = await this.httpClient.post<TModel[]>(`/move`, body, options);

    return this.migrator ? this.migrator.applyArray(data) : data;
  }

  async createChildren(
    id: DocumentId,
    models: DocumentWithChildren<TModel>[],
    config?: AxiosRequestConfig,
  ): Promise<TModel> {
    const _models = this.migrator ? await this.migrator.applyTree(models, true) : models;

    const { data } = await this.httpClient.post<TModel>(`/${id}/children`, _models, config);

    return this.migrator ? this.migrator.apply(data) : data;
  }

  async getChildren(id: DocumentId): Promise<TModel[]> {
    const { data } = await this.httpClient.get<TModel[] | null>(`/${id}/children`);
    const documents = data ?? [];

    return this.migrator ? this.migrator.applyArray(documents) : documents;
  }

  async bisect(id: DocumentId): Promise<TModel[]> {
    const { data } = await this.httpClient.get<TModel[] | null>(`/${id}/bisect`);
    const documents = data ?? [];

    return documents;
  }

  async removeChildren(id: DocumentId, ids: DocumentId[], config?: AxiosRequestConfig) {
    const { data } = await this.httpClient.delete<TModel>(`/${id}/children`, {
      ...config,
      data: ids,
    });

    return this.migrator ? this.migrator.apply(data) : data;
  }

  async createRoot(node: TModel): Promise<TModel> {
    return super.create(node);
  }

  async getTree(id: DocumentId, config?: AxiosRequestConfig) {
    const documents = await this.httpClient
      .get<DocumentWithChildren<TModel>[]>(`/${id}/export`, config)
      .then(({ data }) => data);

    if (this.migrator) {
      return this.migrator.applyTree(documents);
    }

    return documents;
  }

  getChanges(id: DocumentId, since: DocumentRevision) {
    const options = {
      params: {
        since,
      },
    };

    return this.httpClient
      .get<BaseDocumentChangeModel<TModel>[] | null>(`${id}/changes`, options)
      .then(({ data }) => data ?? []);
  }

  getPathToRoot(id?: DocumentId | undefined): Promise<DocumentId[]> {
    if (!id) return Promise.resolve([]);

    return this.httpClient.get<DocumentId[]>(`/${id}/locate`).then(({ data }) => data);
  }

  async updateAccess(model: TModel, request: DocumentAccess[]) {
    const { _id, _rev, meta } = model;
    const config: AxiosRequestConfig = {
      headers: {
        'If-Match': `"${_rev}"`,
      },
    };
    const { headers } = await this.httpClient.put<null, UpdateResponse<null>>(
      `/${_id}/access`,
      request,
      config,
    );

    return {
      ...model,
      meta: {
        ...meta,
        access: request,
      },
      _rev: JSON.parse(headers.etag) as DocumentRevision,
    };
  }

  getAccess(id: DocumentId) {
    return this.httpClient.get<UserAccessModel[]>(`/${id}/access`).then(({ data }) => data);
  }

  async getAttachmentList(docId: DocumentId) {
    return this.httpClient.get<TModel>(`/${docId}`).then(({ data }) => data.attachments);
  }

  async setAttachment(
    docId: DocumentId,
    attachmentId: AttachmentId,
    _rev: DocumentRevision,
    file: File,
  ): Promise<DocumentRevision> {
    const config: AxiosRequestConfig = {
      headers: {
        'If-Match': `"${_rev}"`,
        'Content-Type': file.type,
      },
    };

    const fileData = await fileReadAsync(file);

    const { headers } = await this.httpClient.put<null, UpdateResponse<null>>(
      `/${docId}/attachments/${attachmentId}`,
      fileData,
      config,
    );

    return JSON.parse(headers.etag) as DocumentRevision;
  }

  async deleteAttachment(docId: DocumentId, attachmentId: AttachmentId, _rev: DocumentRevision) {
    const config: AxiosRequestConfig = {
      headers: {
        'If-Match': `"${_rev}"`,
      },
    };

    const { headers } = await this.httpClient.delete<null, UpdateResponse<null>>(
      `/${docId}/attachments/${attachmentId}`,
      config,
    );

    return JSON.parse(headers.etag) as DocumentRevision;
  }

  getAttachment(docId: DocumentId, attachmentId: AttachmentId) {
    return this.httpClient.get<ArrayBuffer>(`/${docId}/attachments/${attachmentId}`, {
      responseType: 'arraybuffer',
    });
  }

  async getSearchConfig() {
    const { data } = await this.httpClient.get<MeiliSearchConfig>(`/meilisearch-key`);

    return {
      ...data,
      expiresAt: new Date(data.expiresAt),
    };
  }
}
